import { Component } from '@theme/component'
import { createGsapUnderlineController } from '@theme/gsap-draw-underline'
import { loadGsapDraw } from '@theme/gsap-runtime'

const imageWithTooltipConfig = {
  autoRotateDefaultDelayMs: 4000,
  selectors: {
    overlays: '[data-image-with-tooltip-overlays]',
    trigger: '[data-draw-underline]'
  },
  visibleClass: 'is-visible'
}

class ImageWithTooltip extends Component {
  #activeIndex = null
  #autoRotateTimer = null
  #motionQuery = null
  #tooltips = []
  #triggers = []
  #underlineController = null

  connectedCallback() {
    super.connectedCallback()
    this.#underlineController = createGsapUnderlineController({
      isConnected: () => this.isConnected,
      loadGsapDraw
    })
    this.bindTriggers()
    this.buildTooltips()
    this.activateIndex(0, { silent: true })
    this.startAutoRotate()
  }

  buildTooltips() {
    const container = this.getTooltipContainer()
    if (!container) return

    container.replaceChildren()
    this.#tooltips = this.buildTooltipElements(container)
  }

  getTooltipContainer() {
    return this.querySelector(imageWithTooltipConfig.selectors.overlays)
  }

  buildTooltipElements(container) {
    const tooltips = []
    for (const trigger of this.#triggers) {
      tooltips.push(this.appendTooltipForTrigger(container, trigger))
    }
    return tooltips
  }

  appendTooltipForTrigger(container, trigger) {
    const content = trigger.dataset.tooltipContent
    if (!content) return null

    const tooltip = this.createTooltipElement(trigger, content)
    container.appendChild(tooltip)
    return tooltip
  }

  createTooltipElement(trigger, content) {
    const tooltip = document.createElement('div')
    tooltip.className = 'image-with-tooltip__tooltip'
    tooltip.style.setProperty(
      '--tooltip-x',
      `${trigger.dataset.tooltipX ?? 50}%`
    )
    tooltip.style.setProperty(
      '--tooltip-y',
      `${trigger.dataset.tooltipY ?? 60}%`
    )
    tooltip.appendChild(this.createTooltipInner(content))
    return tooltip
  }

  createTooltipInner(content) {
    const inner = document.createElement('div')
    inner.className = 'image-with-tooltip__tooltip-inner'
    inner.innerHTML = content
    return inner
  }

  disconnectedCallback() {
    this.stopAutoRotate()
    this.unbindTriggers()
    super.disconnectedCallback()
  }

  bindTriggers() {
    this.unbindTriggers()
    this.#triggers = this.getTriggers()

    for (const [index, trigger] of this.#triggers.entries()) {
      const onEnter = () => {
        this.stopAutoRotate()
        this.activateIndex(index)
      }
      const onLeave = () => {
        this.startAutoRotate()
      }

      trigger.__imgTooltipHandlers = { onEnter, onLeave }
      trigger.addEventListener('mouseenter', onEnter)
      trigger.addEventListener('focusin', onEnter)
      trigger.addEventListener('mouseleave', onLeave)
      trigger.addEventListener('focusout', onLeave)
    }
  }

  unbindTriggers() {
    for (const trigger of this.#triggers) {
      const handlers = trigger.__imgTooltipHandlers
      if (!handlers) continue
      trigger.removeEventListener('mouseenter', handlers.onEnter)
      trigger.removeEventListener('focusin', handlers.onEnter)
      trigger.removeEventListener('mouseleave', handlers.onLeave)
      trigger.removeEventListener('focusout', handlers.onLeave)
      delete trigger.__imgTooltipHandlers
    }

    this.#triggers = []
  }

  getTriggers() {
    return Array.from(
      this.querySelectorAll(imageWithTooltipConfig.selectors.trigger)
    ).filter((trigger) => trigger instanceof HTMLButtonElement)
  }

  activateIndex(index, { silent = false } = {}) {
    if (index < 0 || index >= this.#triggers.length) return
    if (index === this.#activeIndex) return

    if (this.#activeIndex !== null) {
      this.#underlineController.erase(this.#triggers[this.#activeIndex])
      this.hideTooltipAt(this.#activeIndex)
    }

    const trigger = this.#triggers[index]
    if (!trigger) return

    if (!silent) this.#underlineController.draw(trigger)
    this.showTooltipAt(index)
    this.#activeIndex = index
  }

  showTooltipAt(index) {
    const el = this.#tooltips[index]
    if (!el) return
    el.classList.add(imageWithTooltipConfig.visibleClass)
  }

  hideTooltipAt(index) {
    const el = this.#tooltips[index]
    if (!el) return
    el.classList.remove(imageWithTooltipConfig.visibleClass)
  }

  startAutoRotate() {
    this.stopAutoRotate()

    if (this.dataset.autorotate !== 'true') return
    if (this.prefersReducedMotion()) return
    if (this.#triggers.length <= 1) return

    const delay =
      Number(this.dataset.autorotateDelay) ||
      imageWithTooltipConfig.autoRotateDefaultDelayMs

    this.#autoRotateTimer = window.setInterval(() => {
      const next = ((this.#activeIndex ?? -1) + 1) % this.#triggers.length
      this.activateIndex(next)
    }, delay)
  }

  stopAutoRotate() {
    if (!this.#autoRotateTimer) return
    window.clearInterval(this.#autoRotateTimer)
    this.#autoRotateTimer = null
  }

  prefersReducedMotion() {
    if (!this.#motionQuery) {
      this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    }
    return this.#motionQuery.matches
  }
}

if (!customElements.get('image-with-tooltip')) {
  customElements.define('image-with-tooltip', ImageWithTooltip)
}
